import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { ReportExportButton } from "@/components/report-export-button";
import { useOrganizationTimeZone } from "@/hooks/useOrganizationTimeZone";
import { getErrorMessage } from "@/lib/axios";
import { formatOrganizationDay, toOrganizationDay } from "@/lib/organization-date";
import { formatCurrency } from "@/lib/payout-utils";
import { cashflowReportExportClient } from "@/lib/report-export-clients";
import { toast } from "@/lib/toast";
import {
	type CashflowDayResponse,
	ExportFormat,
	exportCashflowReport,
	getCashflow,
} from "@fp/admin-api-client";
import { DateRangePicker } from "@fp/ui/components/application/date-picker/date-range-picker";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import {
	endOfMonth,
	getLocalTimeZone,
	startOfMonth,
	toCalendarDate,
	today,
	type DateValue,
} from "@internationalized/date";
import type { RangeValue } from "@react-types/shared";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";

const now = today(getLocalTimeZone());

const defaultRange: RangeValue<DateValue> = {
	start: startOfMonth(now),
	end: endOfMonth(now),
};

const toDay = (date: DateValue) => toCalendarDate(date).toString();

export const Route = createFileRoute("/reporting/cashflow")({
	component: RouteComponent,
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const [dateRange, setDateRange] =
		useState<RangeValue<DateValue>>(defaultRange);
	const handleDateRangeChange = useCallback(
		(value: RangeValue<DateValue> | null) => {
			if (value) setDateRange(value);
		},
		[],
	);
	const [days, setDays] = useState<CashflowDayResponse[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const timeZoneId = useOrganizationTimeZone();
	const todayInOrganization = toOrganizationDay(
		new Date(),
		timeZoneId ?? getLocalTimeZone(),
	);

	const handleApply = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await getCashflow({
				from: toDay(dateRange.start),
				to: toDay(dateRange.end),
			});
			setDays(response.data);
		} catch (err) {
			toast.error(getErrorMessage(err, "Failed to load the report"));
		} finally {
			setIsLoading(false);
		}
	}, [dateRange]);

	const exportReport = useCallback(
		(format: ExportFormat) =>
			exportCashflowReport({
				from: toDay(dateRange.start),
				to: toDay(dateRange.end),
				format,
			}),
		[dateRange],
	);

	const exportKey = `${toDay(dateRange.start)}-${toDay(dateRange.end)}`;

	return (
		<AdminLayout isWide>
			<AdminPageHeader
				title="Cashflow Report"
				parent={{ url: "/reporting", label: "Reporting" }}
			/>
			<PageContentContainer className="space-y-6">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<DateRangePicker
						defaultOpen
						value={dateRange}
						onChange={handleDateRangeChange}
						onApply={handleApply}
					/>
					{days && (
						<div className="flex gap-2">
							<ReportExportButton key={`csv-${exportKey}`} client={cashflowReportExportClient} format={ExportFormat.Csv} label="CSV" request={exportReport} />
							<ReportExportButton key={`pdf-${exportKey}`} client={cashflowReportExportClient} format={ExportFormat.Pdf} label="PDF" request={exportReport} />
						</div>
					)}
				</div>

				{isLoading && (
					<div className="flex items-center justify-center py-12">
						<div className="text-gray-500">Loading report...</div>
					</div>
				)}

				{!isLoading && !days && (
					<div className="text-center py-12 text-gray-500">
						Select a date range and click Apply to generate the report.
					</div>
				)}

				{!isLoading && days && (
					<CashflowReportView days={days} today={todayInOrganization} />
				)}
			</PageContentContainer>
		</AdminLayout>
	);
}

function CashflowReportView({
	days,
	today,
}: {
	days: CashflowDayResponse[];
	today: string;
}) {
	const hasProjectedDays = days.some((day) => day.day >= today);

	return (
		<div className="space-y-3">
			{hasProjectedDays && (
				<p className="text-sm text-gray-500">
					Days from {formatOrganizationDay(today)} are projected as of now.
				</p>
			)}
			<div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
				<div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-4 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
					<span>Date</span>
					<span className="w-28 text-right">Money In</span>
					<span className="w-28 text-right">Money Out</span>
					<span className="w-36 text-right">End of Day Balance</span>
					<span className="w-16 text-right">Short</span>
					<span className="w-20 text-right">Projected</span>
				</div>
				{days.map((day, index) => (
					<CashflowDayRow
						key={day.day}
						day={day}
						isProjected={day.day >= today}
						isStriped={index % 2 === 1}
					/>
				))}
			</div>
		</div>
	);
}

function CashflowDayRow({
	day,
	isProjected,
	isStriped,
}: {
	day: CashflowDayResponse;
	isProjected: boolean;
	isStriped: boolean;
}) {
	const isShort = day.projectedBalance < 0;

	return (
		<div
			className={`grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-4 px-4 py-2 text-sm ${isStriped ? "bg-gray-50/50" : ""}`}
		>
			<span className="text-gray-700">{formatOrganizationDay(day.day)}</span>
			<span className="w-28 text-right text-gray-900">
				{formatCurrency(day.moneyIn, "USD")}
			</span>
			<span className="w-28 text-right text-gray-900">
				{formatCurrency(day.moneyOut, "USD")}
			</span>
			<span
				className={`w-36 text-right ${isShort ? "text-red-600 font-semibold" : "text-gray-900"}`}
			>
				{formatCurrency(day.projectedBalance, "USD")}
			</span>
			<span className="w-16 text-right text-red-600 font-semibold">
				{isShort ? "Yes" : ""}
			</span>
			<span className="w-20 text-right text-gray-500">
				{isProjected ? "Yes" : ""}
			</span>
		</div>
	);
}
